import Manager from 'coreClasses/Manager';
import {ISeoMetadata, IPageMetaTag, IPageUrls, IJsonLd, ILink} from 'coreStore/data/page/types';
import {
	receiveMetadata,
	selectMetadata,
	receivePageUrls,
	receiveLinks,
	selectPageUrls,
	receiveJsonLd,
	selectJsonLd,
	selectLinks,
	receiveCanonical,
	selectCanonical,
} from 'coreStore/data/page';

/** JSON-LD of a page, as typed by the callers (route info returns a list, see `setJsonLd`). */
type JsonLdData = IJsonLd | IJsonLd[];

/**
 * Represents SeoMgr
 */
class SeoMgr extends Manager {
	private defaultMetadata: ISeoMetadata;

	/**
	 * Sets the default metadata for the site
	 * @param {ISeoMetadata} metadata
	 */
	setDefaultMetadata(metadata: ISeoMetadata) {
		this.defaultMetadata = metadata;
	}

	/**
	 * Sets seo data for a page
	 * @param {ISeoMetadata[]} metadata - Page metadata
	 * @param {IPageUrls|string} pageUrls - Page urls
	 * @param {IJsonLd|IJsonLd[]} jsonLd - JSON-LD data
	 * @param {String} canonical - Canonical url
	 */
	setData = (
		metadata: ISeoMetadata[],
		pageUrls: IPageUrls | string | Record<string, string>,
		jsonLd?: JsonLdData,
		canonical?: string,
	) => {
		if (metadata) {
			this.setMetadata(metadata);
		}

		if (jsonLd) {
			this.setJsonLd(jsonLd);
		}

		if (canonical) {
			this.setCanonicalURL(canonical);
		}

		if (typeof pageUrls === 'string') {
			this.setNonLocalizedPageUrl(pageUrls);
		} else {
			this.setPageUrls(pageUrls || {});
		}
	};

	/**
	 * Replaces the whole SEO state of the page: fields not provided are reset, so nothing leaks from
	 * the previous page (e.g. PLP `prev`/`next` links, JSON-LD, canonical) on client navigation.
	 * Dispatches synchronously: call it from an effect (`useClientServerEffect`), not from render.
	 * @param {object} seo - Page SEO data
	 */
	replaceData = ({metadata, pageUrls, jsonld, canonical, links}: {
		metadata?: ISeoMetadata[];
		pageUrls?: IPageUrls | string | Record<string, string>;
		jsonld?: JsonLdData;
		canonical?: string;
		links?: ILink[];
	}) => {
		const store = this.managerRepository.getStoreMgr().getStore();

		this.setMetadata(metadata || []);
		this.setJsonLd(jsonld || []);
		store.dispatch(receiveCanonical(canonical || ''));
		this.setLinks(links || []);

		if (typeof pageUrls === 'string') {
			this.setNonLocalizedPageUrl(pageUrls);
		} else {
			this.setPageUrls((pageUrls || {}) as IPageUrls);
		}
	};

	/**
	 * Returns stored metadata
	 * @returns {ISeoMetadata}
	 */
	getMetadata = (): ISeoMetadata => {
		return selectMetadata(this.managerRepository.getStoreMgr().getStore().getState());
	};

	/**
	 * Sets metadata from multiple sources - SFCC, CMS, etc.
	 * @param {ISeoMetadata[]} metadataArray
	 */
	setMetadata = (metadataArray: ISeoMetadata[]) => {
		// The default metadata is missing until the app config sets it (tests, code running before `AppConfig`).
		const arrayToProcess: ISeoMetadata[] = [...(metadataArray || []), this.defaultMetadata].filter(Boolean);

		const metadata: ISeoMetadata = {
			pageMetaTags: [],
		};

		arrayToProcess.forEach((metadataObject) => {
			addPageMetaTags(metadata.pageMetaTags, metadataObject?.pageMetaTags);

			Object.keys(metadataObject).forEach((key) => {
				if (key !== 'pageMetaTags' && metadataObject[key] && !metadata[key]) {
					metadata[key] = metadataObject[key];
				}
			});
		});

		this.managerRepository.getStoreMgr().getStore().dispatch(receiveMetadata(metadata));
	};

	/**
	 * Returns stored page urls in different languages
	 * @returns {IPageUrls}
	 */
	getPageUrls = (): IPageUrls => {
		return selectPageUrls(this.managerRepository.getStoreMgr().getStore().getState());
	};

	/**
	 * Stores page urls for different languages
	 * @param {IPageUrls} pageUrls
	 */
	private setPageUrls = (pageUrls: IPageUrls) => {
		this.managerRepository.getStoreMgr().getStore().dispatch(receivePageUrls(pageUrls));
	};

	/**
	 * Returns stored page links
	 * @returns {ILink[]}
	 */
	getLinks = (): ILink[] => {
		return selectLinks(this.managerRepository.getStoreMgr().getStore().getState());
	};

	/**
	 * Stores page links
	 * @param {ILink[]} links - links
	 * @return void
	 */
	setLinks = (links: ILink[]) => {
		this.managerRepository.getStoreMgr().getStore().dispatch(receiveLinks(links));
	};

	/**
	 * Stores page urls for different languages for a simple route
	 * @param {string} url
	 */
	private setNonLocalizedPageUrl = (url: string) => {
		const languages = this.managerRepository.getLanguagesMgr().getLanguages();
		const pageUrls = {};
		languages.map((language) => {
			pageUrls[language.getLanguageCode()] = url;
		});
		this.setPageUrls(pageUrls);
	};

	/**
	 * Sets json ld data. Stored as received: the store and the `JsonLd` component expect the list
	 * returned by the route info, some callers type it as a single entry.
	 * @param {IJsonLd|IJsonLd[]} jsonLd - JSON-LD data
	 */
	private setJsonLd = (jsonLd: JsonLdData) => {
		this.managerRepository.getStoreMgr().getStore().dispatch(receiveJsonLd(jsonLd as IJsonLd[]));
	};

	/**
	 * Returns stored json ld data
	 * @returns {IJsonLd[]}
	 */
	getJsonLd = () => {
		return selectJsonLd(this.managerRepository.getStoreMgr().getStore().getState());
	};

	/**
	 * Clears all stored data from redux
	 * @returns {void}
	 */
	private clearSeoData = () => {
		this.managerRepository.getStoreMgr().getStore().dispatch(receiveMetadata({}));
		this.managerRepository.getStoreMgr().getStore().dispatch(receivePageUrls({}));
		this.managerRepository.getStoreMgr().getStore().dispatch(receiveJsonLd([]));
		this.managerRepository.getStoreMgr().getStore().dispatch(receiveLinks([]));
		this.managerRepository.getStoreMgr().getStore().dispatch(receiveCanonical(''));
	};

	/**
	 * Sets canonical url for the page
	 * @param {String} canonical - canonical url
	 */
	public setCanonicalURL = async (canonical: string) => {
		// Yield before Redux dispatches so they run outside the synchronous
		// render phase of useSuspenseQuery, avoiding "Cannot update component
		// X while rendering component Y" warnings (e.g. HrefLangs).
		await Promise.resolve();

		this.managerRepository.getStoreMgr().getStore().dispatch(receiveCanonical(canonical));
	};

	/**
	 * Returns stored canonical url
	 * @returns {String} - canonical url
	 */
	getCanonicalURL = () => {
		return selectCanonical(this.managerRepository.getStoreMgr().getStore().getState());
	};

	/**
	 * Returns if the page's metadata has noIndex
	 * @returns {boolean} - is noIndex
	 */
	getIsNoIndex = () => {
		const metadata = this.getMetadata();
		const robotsMetaTag = metadata?.pageMetaTags?.find(metaTag => metaTag.ID === 'robots');

		return !!robotsMetaTag?.content.includes('noindex');
	};
}

/**
 * Adds meta tags to page meta tags list, skipping the IDs already present
 * @param {IPageMetaTag[]} pageMetaTags - page meta tags, updated in place
 * @param {IPageMetaTag[]} [metaTagsToAdd] - meta tags to add
 */
function addPageMetaTags(pageMetaTags: IPageMetaTag[], metaTagsToAdd?: IPageMetaTag[]) {
	if (!metaTagsToAdd) {
		return;
	}

	metaTagsToAdd.forEach((metaTag) => {
		if (pageMetaTags.find((pageMetaTag) => pageMetaTag.ID === metaTag.ID)) {
			return;
		}

		pageMetaTags.push(metaTag);
	});
}

export default SeoMgr;
